import React, { useState } from 'react';
import { DataTable } from './components/DataTable';
import { backoffers } from './data/mockData';
import { BackofferForm } from './forms/BackofferForm';
import { BackofferDetail } from './detail/BackofferDetail';
import { Plus, Copy, Archive, ExternalLink, Eye } from 'lucide-react';
export const Backoffers: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedOffer, setSelectedOffer] = useState<any>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const handleCreate = () => {
    setFormMode('create');
    setSelectedOffer(null);
    setIsFormOpen(true);
    setIsDetailOpen(false);
  };
  const handleEdit = (offer: any) => {
    setFormMode('edit');
    setSelectedOffer(offer);
    setIsFormOpen(true);
    setIsDetailOpen(false);
  };
  const handleClone = (offer: any) => {
    // Clone the offer with a new ID and slightly different name
    const clonedOffer = {
      ...offer,
      id: `bo_${Date.now()}`,
      name: `${offer.name} (Clone)`
    };
    // In a real app, you would save this to the backend
    console.log('Cloned offer:', clonedOffer);
    alert(`Cloned offer: ${clonedOffer.name}`);
  };
  const handleArchive = (offer: any) => {
    // In a real app, you would update the status in the backend
    console.log('Archived offer:', offer.id);
    alert(`Archived offer: ${offer.name}`);
  };
  const handleTestClick = (offer: any) => {
    // Open the offer URL in a new tab
    window.open(offer.baseUrl, '_blank');
  };
  const handlePreview = (offer: any) => {
    setPreviewUrl(offer.baseUrl);
  };
  const handleClosePreview = () => {
    setPreviewUrl(null);
  };
  const handleViewDetails = (offer: any) => {
    setSelectedOffer(offer);
    setIsDetailOpen(true);
    setIsFormOpen(false);
  };
  const handleFormSubmit = (formData: any) => {
    // In a real app, you would save this to the backend
    console.log('Form submitted:', formData);
    setIsFormOpen(false);
    alert(`${formMode === 'create' ? 'Created' : 'Updated'} offer: ${formData.name}`);
  };
  const handleFormCancel = () => {
    setIsFormOpen(false);
  };
  const handleDetailClose = () => {
    setIsDetailOpen(false);
  };
  const columns = [{
    id: 'status',
    header: 'Status',
    cell: (row: any) => <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${row.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`} data-id="element-11191">
          {row.status === 'active' ? 'Active' : 'Inactive'}
        </span>
  }, {
    id: 'name',
    header: 'Offer Name',
    cell: (row: any) => <div className="font-medium text-purple-600 dark:text-purple-400" data-id="element-11192">
          {row.name}
        </div>
  }, {
    id: 'verticalId',
    header: 'Vertical',
    cell: (row: any) => {
      const verticalMap: Record<string, string> = {
        cams: 'Cams',
        dating: 'Dating',
        nutra: 'Nutra',
        games: 'Games',
        vpn: 'VPN',
        crypto: 'Crypto',
        cbd: 'CBD',
        adult: 'Adult',
        gambling: 'Gambling'
      };
      return verticalMap[row.verticalId] || row.verticalId;
    }
  }, {
    id: 'geos',
    header: 'GEOs',
    cell: (row: any) => <div className="flex flex-wrap gap-1" data-id="element-11193">
          {row.targeting.geos.map((geo: string) => <span key={geo} className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded" data-id="element-11194">
              {geo.toUpperCase()}
            </span>)}
          {row.targeting.geos.length > 3 && <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400 rounded" data-id="element-11195">
              +{row.targeting.geos.length - 3} more
            </span>}
        </div>
  }, {
    id: 'payout',
    header: 'Payout',
    cell: (row: any) => <div data-id="element-11196">
          <span className="font-medium" data-id="element-11197">${row.payoutAmount}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400 ml-1" data-id="element-11198">
            ({row.payoutModelId.toUpperCase()})
          </span>
        </div>
  }, {
    id: 'epc',
    header: 'EPC',
    cell: (row: any) => <div data-id="element-11199">${row.performance.epc.toFixed(2)}</div>
  }, {
    id: 'cr',
    header: 'CR',
    cell: (row: any) => <div data-id="element-11200">{(row.performance.cr * 100).toFixed(2)}%</div>
  }, {
    id: 'rpm',
    header: 'RPM',
    cell: (row: any) => <div data-id="element-11201">${row.performance.rpm.toFixed(2)}</div>
  }, {
    id: 'caps',
    header: 'Caps',
    cell: (row: any) => <div data-id="element-11202">
          <div className="text-xs" data-id="element-11203">
            Daily: {row.caps.daily.toLocaleString()}
          </div>
          <div className="text-xs" data-id="element-11204">
            Hourly: {row.caps.hourly.toLocaleString()}
          </div>
        </div>
  }, {
    id: 'priority',
    header: 'Priority',
    cell: (row: any) => <div data-id="element-11205">{row.priority}</div>
  }, {
    id: 'revenue',
    header: 'Last 24h Revenue',
    cell: (row: any) => <div className="font-medium" data-id="element-11206">
          ${(row.performance.revenue / 7).toFixed(2)}
        </div>
  }, {
    id: 'actions',
    header: 'Actions',
    cell: (row: any) => <div className="flex space-x-2" data-id="element-11207">
          <button onClick={e => {
        e.stopPropagation();
        handleEdit(row);
      }} className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300" title="Edit" data-id="element-11208">
            Edit
          </button>
          <button onClick={e => {
        e.stopPropagation();
        handleClone(row);
      }} className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300" title="Clone" data-id="element-11209">
            <Copy size={16} data-id="element-11210" />
          </button>
          <button onClick={e => {
        e.stopPropagation();
        handleArchive(row);
      }} className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300" title="Archive" data-id="element-11211">
            <Archive size={16} data-id="element-11212" />
          </button>
          <button onClick={e => {
        e.stopPropagation();
        handleTestClick(row);
      }} className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300" title="Test Click" data-id="element-11213">
            <ExternalLink size={16} data-id="element-11214" />
          </button>
          <button onClick={e => {
        e.stopPropagation();
        handlePreview(row);
      }} className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300" title="Preview Landing" data-id="element-11215">
            <Eye size={16} data-id="element-11216" />
          </button>
        </div>,
    sortable: false
  }];
  return <div data-id="element-11217">
      <div className="flex justify-between items-center mb-6" data-id="element-11218">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-11219">
          Backoffers
        </h1>
        <button onClick={handleCreate} className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center" data-id="element-11220">
          <Plus size={16} className="mr-2" data-id="element-11221" />
          Create Backoffer
        </button>
      </div>
      {!isFormOpen && !isDetailOpen && <DataTable data={backoffers} columns={columns} title="Backoffers" onRowClick={handleViewDetails} data-id="element-11222" />}
      {isFormOpen && <BackofferForm mode={formMode} initialData={selectedOffer} onSubmit={handleFormSubmit} onCancel={handleFormCancel} data-id="element-11223" />}
      {isDetailOpen && selectedOffer && <BackofferDetail offer={selectedOffer} onClose={handleDetailClose} onEdit={() => handleEdit(selectedOffer)} onClone={() => handleClone(selectedOffer)} onArchive={() => handleArchive(selectedOffer)} data-id="element-11224" />}
      {previewUrl && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75" data-id="element-11225">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-hidden flex flex-col" data-id="element-11226">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center" data-id="element-11227">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-11228">
                Landing Page Preview
              </h3>
              <button onClick={handleClosePreview} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" data-id="element-11229">
                &times;
              </button>
            </div>
            <div className="flex-1 overflow-hidden" data-id="element-11230">
              <iframe src={previewUrl} className="w-full h-full" title="Landing Page Preview" data-id="element-11231" />
            </div>
          </div>
        </div>}
    </div>;
};